import { VestmentType, isV1VestmentType } from '../enum/vestmentType';

const STOLE_TYPE_ORDER: VestmentType[] = [
  VestmentType.StoleFlatPriest,
  VestmentType.StoleStandingBackPriest,
  VestmentType.StolePastoralPriest,
  VestmentType.StoleDeacon,
];

export function sortStoleVestments<T extends { type: VestmentType }>(
  vestments: T[]
): T[] {
  const rank = (type: VestmentType) =>
    isV1VestmentType(type)
      ? STOLE_TYPE_ORDER.length
      : STOLE_TYPE_ORDER.indexOf(type);
  return [...vestments].sort((a, b) => rank(a.type) - rank(b.type));
}

export type StoleFeatures = {
  designTypes: VestmentType[];
  hasCuts: boolean;
  allowsCreateCut: boolean;
  patching: boolean;
};

const STOLE_FEATURES: Partial<Record<VestmentType, StoleFeatures>> = {
  [VestmentType.StoleFlatPriest]: {
    designTypes: [VestmentType.StoleFlatPriest],
    hasCuts: false,
    allowsCreateCut: false,
    patching: true,
  },
  [VestmentType.StoleStandingBackPriest]: {
    designTypes: [VestmentType.StoleFlatPriest],
    hasCuts: false,
    allowsCreateCut: false,
    patching: true,
  },
  [VestmentType.StolePastoralPriest]: {
    designTypes: [VestmentType.StolePastoralPriest],
    hasCuts: true,
    allowsCreateCut: true,
    patching: false,
  },
  [VestmentType.StoleDeacon]: {
    designTypes: [VestmentType.StoleDeacon],
    hasCuts: true,
    allowsCreateCut: false,
    patching: true,
  },
  [VestmentType.StoleV1Ordinary]: {
    designTypes: [VestmentType.StoleV1Ordinary],
    hasCuts: false,
    allowsCreateCut: false,
    patching: true,
  },
  [VestmentType.StoleV1Special]: {
    designTypes: [VestmentType.StoleV1Special],
    hasCuts: false,
    allowsCreateCut: false,
    patching: true,
  },
  [VestmentType.StoleV1RomanOrdinary]: {
    designTypes: [VestmentType.StoleV1RomanOrdinary],
    hasCuts: false,
    allowsCreateCut: false,
    patching: true,
  },
  [VestmentType.StoleV1RomanSpecial]: {
    designTypes: [VestmentType.StoleV1RomanSpecial],
    hasCuts: false,
    allowsCreateCut: false,
    patching: true,
  },
  [VestmentType.StoleV1DeaconStraight]: {
    designTypes: [VestmentType.StoleV1DeaconStraight],
    hasCuts: false,
    allowsCreateCut: false,
    patching: true,
  },
  [VestmentType.StoleV1DeaconLShaped]: {
    designTypes: [VestmentType.StoleV1DeaconLShaped],
    hasCuts: false,
    allowsCreateCut: false,
    patching: true,
  },
};

export function getStoleFeatures(
  vestmentType?: VestmentType | null
): StoleFeatures | undefined {
  return vestmentType ? STOLE_FEATURES[vestmentType] : undefined;
}

type StoleFeatureFlag = 'hasCuts' | 'allowsCreateCut' | 'patching';

/**
 * Fields a preset of a design of this type can set: the features of every
 * non-v1 stole type that lists the type's designs. v1 design types have none.
 */
export function getStoleDesignPresetFeatures(
  designType?: VestmentType | null
): StoleFeatures | undefined {
  if (!designType || isV1VestmentType(designType)) {
    return undefined;
  }
  const sources = (Object.keys(STOLE_FEATURES) as VestmentType[])
    .filter((type) => !isV1VestmentType(type))
    .map((type) => STOLE_FEATURES[type])
    .filter(
      (features): features is StoleFeatures =>
        features != null && features.designTypes.includes(designType)
    );
  if (sources.length === 0) {
    return undefined;
  }
  const any = (flag: StoleFeatureFlag) =>
    sources.some((features) => features[flag]);
  return {
    designTypes: [designType],
    hasCuts: any('hasCuts'),
    allowsCreateCut: any('allowsCreateCut'),
    patching: any('patching'),
  };
}
